import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const CardModal = ({ card, onClose }) => {
  const [errored, setErrored] = useState(false);
  React.useEffect(() => { setErrored(false); }, [card]);

  return (
    <AnimatePresence>
      {card && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="modal-backdrop"
          onClick={onClose}
          data-testid="card-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-4xl my-10 relative overflow-hidden"
            style={{ background: "#fff", color: "#1f2937", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition"
              style={{ background: "rgba(19,26,72,0.08)" }}
              data-testid="card-modal-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" style={{ color: "var(--cs-navy)" }} />
            </button>

            <div className="grid md:grid-cols-5 gap-0">
              {/* Image */}
              <div className="md:col-span-2 flex items-center justify-center p-6 md:p-8" style={{ background: "#F1F4FA" }}>
                <div className="relative w-full aspect-[5/8] rounded-2xl overflow-hidden border" style={{ borderColor: "rgba(19,26,72,0.12)", boxShadow: "0 12px 36px rgba(19,26,72,0.15)" }}>
                  {!errored ? (
                    <img
                      src={card.imageUrl}
                      alt={card.name}
                      className="w-full h-full object-cover"
                      onError={() => setErrored(true)}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-center p-4"
                         style={{ background: "linear-gradient(135deg,#25478e,#131a48)", color: "#fff", fontFamily: "'Cormorant Garamond', serif" }}>
                      <div>
                        <div className="text-xs uppercase tracking-widest opacity-70 mb-1">
                          {card.arcana === "major" ? "Major Arcana" : card.suit}
                        </div>
                        <div className="text-2xl italic">{card.name}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Detail */}
              <div className="md:col-span-3 p-6 md:p-10 max-h-[80vh] md:max-h-[78vh] overflow-y-auto">
                <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "var(--cs-purple)" }}>
                  {card.arcana === "major" ? `MAJOR ARCANA · ${card.n}` : `${card.suit.toUpperCase()} · ${card.label}`}
                </p>
                <h3 className="font-serif-display text-4xl md:text-5xl leading-tight mb-2" style={{ color: "var(--cs-navy)" }} data-testid="card-modal-name">
                  {card.name}
                </h3>
                <p className="text-sm mb-5" style={{ color: "#64748B" }}>Element: <span style={{ color: "var(--cs-blue-dk)" }} className="font-medium">{card.element}</span></p>

                <div className="mb-6">
                  <h4 className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: "var(--cs-purple)" }}>Keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {card.keywords.map((k) => (
                      <span key={k} className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full"
                            style={{ background: "rgba(153,76,153,0.08)", border: "1px solid rgba(153,76,153,0.20)", color: "var(--cs-purple)" }}>
                        {k}
                      </span>
                    ))}
                  </div>
                </div>

                <section className="mb-6">
                  <h4 className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: "var(--cs-blue-dk)" }}>Description</h4>
                  <p className="leading-relaxed" style={{ color: "#334155" }}>{card.description}</p>
                </section>

                <section className="mb-6">
                  <h4 className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: "var(--cs-blue)" }}>Upright</h4>
                  <p className="leading-relaxed" style={{ color: "#334155" }}>{card.upright}</p>
                </section>

                <section>
                  <h4 className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: "var(--cs-rose)" }}>Reversed</h4>
                  <p className="leading-relaxed" style={{ color: "#334155" }}>{card.reversed}</p>
                </section>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CardModal;
